import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardTitle, Grid, MetricCard, Pill, Btn, Insight } from '../components/UI';
import { supabase } from '../supabaseClient';

// Pathway routes — the human gate for route-level Pathways data (26 Oct 2026).
// Owner: "Reviews should be reviewed and accepted in Sanoire Admin, anything
// accepted should be immediately in the app." Accept per flagged field; a
// route goes live when published; any field still open shows in the app as
// "Not yet confirmed". See api/routes-review.js.

const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';
const SECTION_LABEL = {
  summary: 'Summary', money: 'What you need to show', age: 'Age', family: 'Family',
  conditions: 'Other requirements', lasts: 'How long it lasts', leads_to: 'Leads to',
  work: 'Work', fees: 'Fees', apply: 'Where to apply', processing: 'Processing time',
  status: 'Changes and caveats', route: 'The whole route',
  naturalisation: 'Citizenship by residence', descent: 'Citizenship by descent',
  buying: 'Buying property', tax: 'Tax residence', regions: 'Regional differences',
};
const STATUS_PILL = { wrong: 'red', outdated: 'amber', conflict: 'amber', unsupported: 'blue', unreachable: 'blue' };
const STATE_PILL = { awaiting: 'amber', published: 'green', rejected: 'red', withheld: 'red' };
const COUNTRY = { THA: 'Thailand', MYS: 'Malaysia', ITA: 'Italy', MEX: 'Mexico', ARE: 'United Arab Emirates' };

function show(v) {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'string') return v;
  try { return JSON.stringify(v); } catch (_e) { return String(v); }
}

function Lines({ lines }) {
  if (!lines || !lines.length) return <span style={{ color: '#9aa7ae' }}>—</span>;
  return <div>{lines.map((l, i) => <div key={i} style={{ fontSize: 13, color: '#1f3440', lineHeight: 1.45 }}>{l}</div>)}</div>;
}

// Verdict rules in plain English, so the reviewer reads exactly what the app
// will test a member against (26 Oct 2026, Pathways v5).
const KIND_LABEL = {
  remote_employee: 'employed remotely by a company abroad', freelance_foreign: 'freelance for clients abroad',
  business_abroad: 'own business abroad', pension: 'pension', investments: 'investment income', salary_local: 'salary from a local employer',
};
const money = (a, ccy) => `${ccy} ${Number(a).toLocaleString('en-GB', { maximumFractionDigits: 2 })}`;
const kinds = (k) => (k || []).map((x) => KIND_LABEL[x] || x).join(' or ');
function describeCond(c) {
  switch (c.t) {
    case 'income': return `Income of ${money(c.amount, c.ccy)} a ${c.period}` + (c.kinds ? ` from ${kinds(c.kinds)}` : ' from any source')
      + (c.kinds_maybe ? `; "Depends" if from ${kinds(c.kinds_maybe)}` : '') + (c.lower_amount ? `; "Depends" from ${money(c.lower_amount, c.ccy)}` : '');
    case 'savings': return `Funds of ${money(c.amount, c.ccy)}`;
    case 'age': return `Aged ${c.min} or over`;
    case 'job_offer': return 'A job offer from an employer there';
    case 'activity': return `Activity: ${c.text} (met by ${kinds(c.kinds)}; otherwise "Depends")`;
    case 'apply_where_resident': return 'Apply where you are a permanent resident ("Depends" if the member lives outside their passport country)';
    case 'passports_allowed': return `Only these passports: ${c.iso3.join(', ')}`;
    case 'passports_excluded': return `Not these passports: ${c.iso3.join(', ')}`;
    case 'income_kind': return `Income must be ${kinds(c.kinds)}`;
    case 'confirm': return `Member confirms: ${c.text}`;
    case 'depends': return `Always "Depends": ${c.text}`;
    case 'unknown': return `No answer: ${c.text}`;
    case 'descent': return `Descent from ${c.country}, up to generation ${c.max_generation}: ${c.text}`;
    default: return JSON.stringify(c);
  }
}

function RulesBlock({ row, busy, onRules }) {
  const [note, setNote] = useState('');
  const req = row.requirements;
  if (!req) return null;
  const pill = { awaiting: 'amber', accepted: 'green', rejected: 'red' }[row.requirements_state] || 'blue';
  return (
    <div style={{ borderTop: '1px solid #eef2f4', marginTop: 10, paddingTop: 8 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: '#7a5a1a' }}>Verdict rules</div>
        <Pill type={pill}>{row.requirements_state}</Pill>
        <span style={{ fontSize: 12, color: '#6b7a83' }}>Used for "Meets the requirements" answers only once accepted.</span>
      </div>
      {(req.options || []).map((o, i) => (
        <div key={i} style={{ marginTop: 6, fontSize: 12.5, color: '#1f3440' }}>
          <b>{req.options.length > 1 ? `Option ${i + 1}` : 'Requirements'}</b> (all must hold)
          {(o.all || []).map((c, j) => <div key={j} style={{ marginLeft: 12 }}>· {describeCond(c)}</div>)}
        </div>
      ))}
      {req.per_dependant ? <div style={{ fontSize: 12.5, marginTop: 4 }}>Per dependant ({req.per_dependant.who === 'spouse' ? 'spouse only' : 'each'}): {req.per_dependant.income ? money(req.per_dependant.income.amount, req.per_dependant.income.ccy) + ' a ' + req.per_dependant.income.period + ' more income' : ''}{req.per_dependant.savings ? '; ' + money(req.per_dependant.savings.amount, req.per_dependant.savings.ccy) + ' more funds' : ''}</div> : null}
      <div style={{ fontSize: 12.5, marginTop: 4 }}>Family can join: {req.family === true ? 'yes' : req.family === false ? 'no' : 'not sourced ("Depends" for families)'}</div>
      {(req.notes || []).map((n, i) => <div key={i} style={{ fontSize: 12.5, marginTop: 2 }}>Note: {n}</div>)}
      {req.conflict ? <div style={{ fontSize: 12.5, marginTop: 2 }}>Sources disagree (caps the verdict at "Depends"): {req.conflict}</div> : null}
      {row.requirements_state !== 'accepted' ? (
        <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
          <Btn small variant="green" disabled={busy} onClick={() => onRules(row, 'accept', '')}>Accept rules</Btn>
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why not (needed to reject)" style={{ flex: 1, minWidth: 160, fontFamily: F, fontSize: 12.5, padding: 6, borderRadius: 7, border: '1px solid #dde8ed' }} />
          <Btn small variant="danger" disabled={busy || !note.trim()} onClick={() => onRules(row, 'reject', note)}>Reject rules</Btn>
        </div>
      ) : <div style={{ fontSize: 12, color: '#6b7a83', marginTop: 4 }}>Accepted by {row.requirements_by}</div>}
    </div>
  );
}

function FlagRow({ f, busy, onResolve }) {
  const [mode, setMode] = useState(null); // 'checker' | 'custom' | 'dismissed'
  const [text, setText] = useState('');
  const [url, setUrl] = useState('');
  const [note, setNote] = useState('');
  const open = f.resolution === 'open';
  const start = (m) => {
    setMode(m);
    if (m === 'checker' || m === 'custom') setText(((f.suggested_lines || []).join('\n')));
    if (m === 'custom') setUrl(f.evidence_url || '');
  };
  const submit = () => {
    const lines = text.split('\n').map((s) => s.trim()).filter(Boolean);
    onResolve(f, mode, { lines, source_url: url || undefined, note: note || undefined });
  };
  return (
    <div style={{ border: '1px solid #e6ecef', borderRadius: 9, padding: '10px 12px', marginTop: 8, background: open ? '#fffdf8' : '#fafbfb' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <Pill type={STATUS_PILL[f.check_status] || 'blue'}>{f.check_status}</Pill>
        <span style={{ fontSize: 12.5, color: '#2f4552', fontWeight: 600 }}>{f.field_label}</span>
        {!open ? <span style={{ marginLeft: 'auto', fontSize: 12, color: '#6b7a83' }}>{f.resolution} · {f.resolved_by}</span> : null}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 8, fontSize: 12.5 }}>
        <div><div style={{ color: '#7a5a1a', fontSize: 11, fontWeight: 700 }}>RESEARCHED</div><div style={{ color: '#2f4552', wordBreak: 'break-word' }}>{show(f.researched_value)}</div></div>
        <div><div style={{ color: '#7a5a1a', fontSize: 11, fontWeight: 700 }}>CHECKER</div><div style={{ color: '#2f4552', wordBreak: 'break-word' }}>{show(f.checker_value)}</div></div>
      </div>
      {f.evidence_quote ? <div style={{ fontSize: 12, color: '#4a5d68', marginTop: 6, fontStyle: 'italic' }}>“{f.evidence_quote}”</div> : null}
      {f.note ? <div style={{ fontSize: 12, color: '#4a5d68', marginTop: 4 }}>{f.note}</div> : null}
      {f.evidence_url ? <a href={f.evidence_url} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#314F5C' }}>Open the evidence ›</a> : null}
      {open && !mode ? (
        <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
          {f.kind === 'field' ? <Btn small variant="outline" disabled={busy} onClick={() => onResolve(f, 'research', {})}>Keep research</Btn> : null}
          {f.kind === 'field' ? <Btn small disabled={busy} onClick={() => start('checker')}>Use correction</Btn> : null}
          {f.kind === 'field' ? <Btn small variant="outline" disabled={busy} onClick={() => start('custom')}>Own wording</Btn> : null}
          <Btn small variant="danger" disabled={busy} onClick={() => start('dismissed')}>{f.kind === 'field' ? 'Dismiss' : 'Note and close'}</Btn>
        </div>
      ) : null}
      {open && mode ? (
        <div style={{ marginTop: 8 }}>
          {mode !== 'dismissed' ? (
            <>
              <div style={{ fontSize: 11.5, color: '#6b7a83', marginBottom: 4 }}>What members will read in “{SECTION_LABEL[f.section] || f.section}”, one line per row. It replaces the whole section.</div>
              <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} style={{ width: '100%', fontFamily: F, fontSize: 13, padding: 8, borderRadius: 7, border: '1px solid #dde8ed' }} />
              {mode === 'custom' ? <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Official source link (required)" style={{ width: '100%', marginTop: 6, fontFamily: F, fontSize: 13, padding: 8, borderRadius: 7, border: '1px solid #dde8ed' }} /> : null}
            </>
          ) : (
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why (required)" style={{ width: '100%', fontFamily: F, fontSize: 13, padding: 8, borderRadius: 7, border: '1px solid #dde8ed' }} />
          )}
          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
            <Btn small disabled={busy} onClick={submit}>Accept</Btn>
            <Btn small variant="outline" disabled={busy} onClick={() => setMode(null)}>Cancel</Btn>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Subject({ kind, row, flags, locales, busy, onResolve, onState, onTranslate, onRules, tstate }) {
  const [note, setNote] = useState('');
  const eff = row.effective || { sections: {} };
  const sectionKeys = Array.from(new Set([
    ...(eff.summary !== null || (row.open_sections || []).includes('summary') ? ['summary'] : []),
    ...Object.keys((row.display_en || {}).sections || {}),
    ...(row.open_sections || []),
    ...flags.filter((f) => f.kind === 'field').map((f) => f.section),
  ].filter(Boolean)));
  const missing = locales.filter((l) => !(row.translated || []).includes(l));
  const openCount = flags.filter((f) => f.resolution === 'open').length;
  const title = kind === 'facts' ? 'Rules for everyone' : ((row.display_en || {}).name || row.route_id);
  return (
    <Card style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#163040' }}>{title}</div>
        <Pill type={STATE_PILL[row.state] || 'blue'}>{row.state}</Pill>
        {openCount ? <Pill type="amber">{openCount} open</Pill> : <Pill type="green">no open flags</Pill>}
        {row.state === 'published' ? <Pill type={missing.length ? 'amber' : 'green'}>{locales.length - missing.length}/{locales.length} translated</Pill> : null}
        <span style={{ marginLeft: 'auto', fontSize: 12, color: '#6b7a83' }}>{kind === 'route' ? row.route_id : row.iso3}</span>
      </div>
      {kind === 'route' && (row.display_en || {}).plain ? <div style={{ fontSize: 13, color: '#4a5d68', marginTop: 2 }}>{row.display_en.plain}</div> : null}

      {sectionKeys.map((k) => {
        const isOpen = (row.open_sections || []).includes(k);
        const fl = flags.filter((f) => f.section === k);
        const lines = k === 'summary' ? (eff.summary ? [eff.summary] : null) : (eff.sections || {})[k];
        return (
          <div key={k} style={{ borderTop: '1px solid #eef2f4', marginTop: 10, paddingTop: 8 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: '#7a5a1a' }}>{SECTION_LABEL[k] || k}</div>
              {isOpen ? <Pill type="amber">Members see “Not yet confirmed”</Pill> : null}
              {(row.corrections || {})[k] ? <Pill type="teal">corrected</Pill> : null}
            </div>
            {isOpen ? <div style={{ fontSize: 12.5, color: '#9aa7ae', marginTop: 4 }}>Researched text, hidden from members until resolved:</div> : null}
            <div style={{ marginTop: 4, opacity: isOpen ? 0.6 : 1 }}>
              <Lines lines={isOpen ? (k === 'summary' ? [(row.display_en || {}).summary] : ((row.display_en || {}).sections || {})[k]) : lines} />
            </div>
            {fl.map((f) => <FlagRow key={f.id} f={f} busy={busy} onResolve={onResolve} />)}
          </div>
        );
      })}

      {kind === 'route' ? <RulesBlock row={row} busy={busy} onRules={onRules} /> : null}

      <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        {row.state !== 'published' ? <Btn variant="green" disabled={busy} onClick={() => onState(kind, row, 'publish', '')}>Publish to the app</Btn> : null}
        {row.state === 'published' && missing.length ? <Btn variant="outline" disabled={busy || !!tstate} onClick={() => onTranslate(kind, row, missing)}>Translate {missing.length} missing</Btn> : null}
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (needed to reject or withhold)" style={{ flex: 1, minWidth: 180, fontFamily: F, fontSize: 12.5, padding: 7, borderRadius: 7, border: '1px solid #dde8ed' }} />
        {row.state === 'awaiting' ? <Btn variant="danger" disabled={busy || !note.trim()} onClick={() => onState(kind, row, 'reject', note)}>Reject</Btn> : null}
        {row.state === 'published' ? <Btn variant="danger" disabled={busy || !note.trim()} onClick={() => onState(kind, row, 'withhold', note)}>Withhold</Btn> : null}
      </div>
      {tstate ? <div style={{ fontSize: 12, color: '#4a5d68', marginTop: 6 }}>{tstate}</div> : null}
    </Card>
  );
}

export default function PathwayRoutes() {
  const [summary, setSummary] = useState(null);
  const [iso3, setIso3] = useState('THA');
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [tstates, setTstates] = useState({}); // subject key -> progress text

  const authHeaders = useCallback(async () => {
    const { data: s } = await supabase.auth.getSession();
    const token = s && s.session && s.session.access_token;
    return token ? { Authorization: 'Bearer ' + token } : {};
  }, []);

  const load = useCallback(async (code) => {
    setErr('');
    try {
      const r = await fetch('/api/routes-review?iso3=' + encodeURIComponent(code), { headers: await authHeaders() });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'load failed');
      setSummary(j.countries || []);
      setDetail(j);
    } catch (e) { setErr(e.message); }
  }, [authHeaders]);
  useEffect(() => { load(iso3); }, [load, iso3]);

  async function post(body) {
    const r = await fetch('/api/routes-review', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
      body: JSON.stringify(body),
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j.error || ('failed: ' + r.status));
    return j;
  }

  // Every locale, three at a time. A refused locale stays on English in the
  // app and is listed here so it can be retried.
  const translateAll = useCallback(async (type, id, locales) => {
    const key = type + ':' + id;
    const failed = [];
    const progress = { done: 0 };
    setTstates((s) => ({ ...s, [key]: `Translating 0/${locales.length}…` }));
    const queue = locales.slice();
    const worker = async () => {
      while (queue.length) {
        const l = queue.shift();
        try { await post({ action: 'translate', type, id, locale: l }); } catch (e) { failed.push(l + ' (' + e.message + ')'); }
        progress.done += 1;
        const n = progress.done;
        setTstates((s) => ({ ...s, [key]: `Translating ${n}/${locales.length}…` }));
      }
    };
    await Promise.all([worker(), worker(), worker()]);
    setTstates((s) => ({ ...s, [key]: failed.length ? 'Not translated, shown in English for now: ' + failed.join('; ') : 'All languages up to date.' }));
    load(iso3);
  }, [iso3, load]); // eslint-disable-line react-hooks/exhaustive-deps

  async function onResolve(f, choice, extra) {
    if (busy) return;
    setBusy(true); setErr(''); setMsg('');
    try {
      const j = await post({ action: 'resolve', id: f.id, choice, ...extra });
      setMsg('Saved. ' + (j.type ? 'Live in the app now if this is published.' : ''));
      await load(iso3);
      // A correction on a published subject changes the English: re-translate.
      if (j.type) {
        const subj = j.type === 'route' ? (detail.routes || []).find((r) => r.route_id === j.id) : detail.facts;
        if (subj && subj.state === 'published') translateAll(j.type, j.id, detail.locales || []);
      }
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }

  async function onRules(row, decision, note) {
    if (busy) return;
    setBusy(true); setErr(''); setMsg('');
    try {
      await post({ action: 'rules', id: row.route_id, decision, note });
      setMsg(decision === 'accept' ? 'Rules accepted: the app now answers this route for each member.' : 'Rules rejected.');
      await load(iso3);
      // The rule texts are translated with the route; refresh them.
      if (decision === 'accept' && row.state === 'published') translateAll('route', row.route_id, (detail && detail.locales) || []);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }

  async function onState(kind, row, action, note) {
    if (busy) return;
    setBusy(true); setErr(''); setMsg('');
    const id = kind === 'route' ? row.route_id : row.iso3;
    try {
      await post({ action, type: kind, id, note });
      setMsg(action === 'publish' ? 'Published. It is in the app now, in English; translating the other languages.' : (action === 'reject' ? 'Rejected.' : 'Withheld: removed from the app.'));
      await load(iso3);
      if (action === 'publish') translateAll(kind, id, detail.locales || []);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }

  const totals = useMemo(() => {
    const t = { awaiting: 0, published: 0, open: 0 };
    for (const c of summary || []) { t.awaiting += c.awaiting; t.published += c.published; t.open += c.open_flags; }
    return t;
  }, [summary]);

  const flagsFor = (routeId) => ((detail && detail.flags) || []).filter((f) => (routeId ? f.route_id === routeId : (!f.route_id && f.kind === 'field')));
  const missingRoutes = ((detail && detail.flags) || []).filter((f) => f.kind === 'missing_route');

  return (
    <div style={{ fontFamily: F }}>
      <Grid cols={3}>
        <MetricCard label="Waiting for you" value={summary ? String(totals.awaiting) : '…'} sub="routes not yet published" accent="#314F5C" />
        <MetricCard label="Open flags" value={summary ? String(totals.open) : '…'} sub="figures the check did not confirm" accent="#9F7E4F" />
        <MetricCard label="Live in the app" value={summary ? String(totals.published) : '…'} sub="published routes" accent="#1aa36b" />
      </Grid>

      <Insight type="info">
        <b>Each route was researched from official pages, then every figure was re-checked by a separate pass.</b> What the
        check did not confirm is listed under its section. <b>Keep research</b>, <b>Use correction</b> (edit the suggested
        wording first) or write <b>Own wording</b> with a source. <b>Publish</b> puts the route on phones within seconds;
        any section with an open flag shows there as “Not yet confirmed” until you resolve it. Translations into the 15 other
        languages run straight after; a language that fails the number check stays in English and is listed for a retry.
      </Insight>

      {err ? <Insight type="warn">{err}</Insight> : null}
      {msg ? <Insight type="ok">{msg}</Insight> : null}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '4px 0 14px' }}>
        {(summary || []).map((c) => (
          <button key={c.iso3} onClick={() => setIso3(c.iso3)} style={{
            fontFamily: F, fontSize: 13, padding: '7px 12px', borderRadius: 100, cursor: 'pointer',
            border: '1px solid ' + (c.iso3 === iso3 ? '#163040' : '#dde8ed'),
            background: c.iso3 === iso3 ? '#163040' : '#fff', color: c.iso3 === iso3 ? '#F2EEE7' : '#163040',
          }}>
            {COUNTRY[c.iso3] || c.iso3} · {c.published}/{c.routes} live{c.open_flags ? ' · ' + c.open_flags + ' open' : ''}
          </button>
        ))}
        <Btn variant="outline" small onClick={() => load(iso3)}>Refresh</Btn>
      </div>

      {detail && detail.facts ? (
        <Subject kind="facts" row={detail.facts} flags={flagsFor(null)} locales={detail.locales || []} busy={busy}
          onResolve={onResolve} onState={onState} onTranslate={(k, r, ls) => translateAll(k, r.iso3, ls)} tstate={tstates['facts:' + detail.facts.iso3]} />
      ) : null}

      {((detail && detail.routes) || []).map((r) => (
        <Subject key={r.route_id} kind="route" row={r} flags={flagsFor(r.route_id)} locales={detail.locales || []} busy={busy}
          onResolve={onResolve} onState={onState} onRules={onRules} onTranslate={(k, row, ls) => translateAll(k, row.route_id, ls)} tstate={tstates['route:' + r.route_id]} />
      ))}

      {missingRoutes.length ? (
        <Card>
          <CardTitle>Routes the check found missing</CardTitle>
          <div style={{ fontSize: 12.5, color: '#4a5d68' }}>Not in the app. Close each with a note once it is queued for research.</div>
          {missingRoutes.map((f) => <FlagRow key={f.id} f={f} busy={busy} onResolve={onResolve} />)}
        </Card>
      ) : null}
    </div>
  );
}
